import { Controller } from "@hotwired/stimulus"
import * as d3 from 'd3'
import LineDrawer from "./line_chart/line_drawer"
import AxisDrawer from "./cartesian/drawers/axis_drawer"
import WeatherLayerDrawer from "./layers/weather_layer_drawer"
import LineChartResponse from "./line_chart/line_chart_response"

// Connects to data-controller="line-chart"
export default class extends Controller {
    static targets = ["xAxisOptionsContainer", "xAxisOptions", "yAxisOptionsContainer", "yAxisOptions",
                      "chart", "chartHeader", "legendContainer", "legendHelpText"]

    static outlets = ["request"]
    static values = { xAxis: String, yAxis: String }

    async connect() {
        this.connected = true
        this.hiddenLineKeys ||= new Set()
        this.initializedLineKeys = new Set()
        this.queueResize = this.queueResize.bind(this)
        const requestNumber = this.refreshRequestNumber = (this.refreshRequestNumber || 0) + 1
        const chartAttributes = await this.requestOutlet.sendGetRequest({url: this.element.dataset.url})
        if (!this.connected) return

        if (requestNumber === this.refreshRequestNumber) {
            this.chartResponse = new LineChartResponse(chartAttributes)
            await this.draw()
        }
        if (!this.connected) return

        this.resizeObserver = new ResizeObserver(this.queueResize)
        this.resizeObserver.observe(this.element)
    }

    disconnect() {
        this.connected = false
        this.refreshRequestNumber++
        this.resizeObserver?.disconnect()
        clearTimeout(this.resizeTimeout)
        cancelAnimationFrame(this.resizeFrame)
    }

    async refresh({ detail: { params, preserveVisibility = false } }) {
        const url = new URL(this.element.dataset.url, window.location.origin)
        Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value ?? ""))
        this.element.dataset.url = url.toString()
        const requestNumber = this.refreshRequestNumber = (this.refreshRequestNumber || 0) + 1
        const attributes = await this.requestOutlet.sendGetRequest({ url: url.toString() })
        if (!this.connected || requestNumber !== this.refreshRequestNumber) return
        this.chartResponse = new LineChartResponse(attributes)
        this.xAxisValue = this.chartResponse.configuration.defaultAxisLabel("x")
        this.yAxisValue = this.chartResponse.configuration.defaultAxisLabel("y")
        if (!preserveVisibility) {
            this.hiddenLineKeys.clear()
            this.initializedLineKeys.clear()
        }
        await this.draw()
    }

    queueResize() {
        if (!this.connected || !this.chartResponse) return

        clearTimeout(this.resizeTimeout)
        this.resizeTimeout = setTimeout(() => {
            cancelAnimationFrame(this.resizeFrame)
            this.resizeFrame = requestAnimationFrame(() => this.draw())
        }, 150)
    }

    async draw() {
        if (!this.connected || !this.chartResponse || !this.element.clientWidth || !this.element.clientHeight) return

        this.setChartState()
        LineDrawer.drawLegends({ lines: this.lines, legendsContainer: this.legendContainerTarget, options: this.chartResponse.options })
        this.bindLegendInteractions()
        this.setSizes()

        const axises = AxisDrawer.drawAxises({
            chartController: this,
            configuration: this.chartResponse.configuration,
            series: this.lines,
            visibleSeries: this.lines.filter(line => !this.hiddenLineKeys.has(line.key))
        })
        this.xScale = axises.xScale
        this.yScale = axises.yScale
        this.secondaryYScale = axises.secondaryYScale
        this.xAxisScaleType = axises.xAxisScaleType
        this.yAxisScaleType = axises.yAxisScaleType
        this.xFormatter = axises.xFormatter
        this.yFormatter = axises.yFormatter
        this.xValueFormatter = axises.xValueFormatter
        this.yValueFormatter = axises.yValueFormatter
        this.secondaryYValueFormatter = axises.secondaryYValueFormatter
        this.margin = axises.margin
        this.innerWidth = axises.innerWidth
        this.innerHeight = axises.innerHeight
        this.chartG = d3.select(this.chartTarget).append("g").attr("transform", `translate(${this.margin.left}, ${this.margin.top + this.layersAboveSize})`);

        this.linesData = LineDrawer.drawLines(
            {
                g: this.chartG,
                xAxis: { scale: this.xScale, scaleType: this.xAxisScaleType },
                yAxis: { scale: this.yScale, scaleType: this.yAxisScaleType },
                secondaryYAxis: { scale: this.secondaryYScale, scaleType: this.yAxisScaleType },
                lines: this.lines,
                legendsContainer: this.legendContainerTarget,
                dataPoints: { show: this.chartResponse.options.show_data_points, radius: this.chartResponse.options.data_point_radius },
                options: this.chartResponse.options,
                hiddenLineKeys: this.hiddenLineKeys
            }
        )
        this.bindAxisHoverInteractions()

        await this.weatherLayerDrawer.draw({
            svg: d3.select(this.chartTarget),
            xAxis: { scale: this.xScale, scaleType: this.xAxisScaleType },
            layersAboveSize: this.layersAboveSize,
            margin: this.margin,
            innerWidth: this.innerWidth,
            innerHeight: this.innerHeight,
            dataXPositions: this.linesData[0]?.map(([x]) => x + this.margin.left) || []
        })

        this.drawHoverOverlay()
    }

    setSizes() {
        this.containerSize = this.element.getBoundingClientRect()
        this.containerHeaderSize = this.chartHeaderTarget.getBoundingClientRect()
        this.legendContainerSize = this.legendContainerTarget.getBoundingClientRect()
        this.legendHelpTextSize = this.hasLegendHelpTextTarget ? this.legendHelpTextTarget.getBoundingClientRect() : { height: 0 }
        const { xDomain, xScaleType } = this.currentXAxisDomain()
        const { size: layersSize, aboveSize: layersAboveSize, drawer: weatherLayerDrawer } = WeatherLayerDrawer.getSizeAndInstance(this.layers, { xDomain, xScaleType })
        this.layersSize = layersSize
        this.layersAboveSize = layersAboveSize
        this.weatherLayerDrawer = weatherLayerDrawer

        const styles = getComputedStyle(this.element)
        const headerStyles = getComputedStyle(this.chartHeaderTarget)
        const legendsStyles = getComputedStyle(this.legendContainerTarget)
        const legendHelpTextStyles = this.hasLegendHelpTextTarget ? getComputedStyle(this.legendHelpTextTarget) : { marginTop: 0, marginBottom: 0 }

        this.svgSize = {
            width: Math.max(
                this.element.clientWidth - this.pixelValue(styles.paddingLeft) - this.pixelValue(styles.paddingRight),
                WeatherLayerDrawer.minimumWidthFor(this.layers)
            ),
            height: this.element.clientHeight - this.pixelValue(styles.paddingTop) - this.pixelValue(styles.paddingBottom) -
                    this.containerHeaderSize.height - this.pixelValue(headerStyles.marginTop) - this.pixelValue(headerStyles.marginBottom) -
                    this.legendContainerSize.height - this.pixelValue(legendsStyles.marginTop) - this.pixelValue(legendsStyles.marginBottom) -
                    this.legendHelpTextSize.height - this.pixelValue(legendHelpTextStyles.marginTop) - this.pixelValue(legendHelpTextStyles.marginBottom)
        }

        this.chartSize = {
            width: this.svgSize.width,
            height: this.svgSize.height - this.layersSize
        }
        this.chartTarget.setAttribute("width", this.svgSize.width)
        this.chartTarget.setAttribute("height", this.svgSize.height)
        this.chartTarget.style.overflow = "visible"
    }

    pixelValue(value) {
        return parseFloat(value) || 0
    }

    setChartState() {
        if (!this.hasXAxisValue) this.xAxisValue = this.chartResponse.configuration.defaultAxisLabel("x")
        if (!this.hasYAxisValue) this.yAxisValue = this.chartResponse.configuration.defaultAxisLabel("y")
        this.focusedYAxis = null

        d3.select(this.chartTarget).selectAll("*").remove()
        this.legendContainerTarget.innerHTML = ''

        this.lines = this.chartResponse.linesFor({
            xAxisLabel: this.xAxisValue,
            yAxisLabel: this.yAxisValue
        })
        this.lines.forEach(line => {
            if (!this.initializedLineKeys.has(line.key)) {
                const visible = this.visibleSeriesKeys ? this.visibleSeriesKeys.includes(line.yKey) : line.options.visible !== false
                if (!visible) this.hiddenLineKeys.add(line.key)
            }
            this.initializedLineKeys.add(line.key)
        })
        this.layers = this.chartResponse.layers
    }

    setSeriesVisibility(keys) {
        this.visibleSeriesKeys = keys
        if (!this.lines) return

        this.lines.forEach(line => {
            if (keys.includes(line.yKey)) this.hiddenLineKeys.delete(line.key)
            else this.hiddenLineKeys.add(line.key)
        })
        this.draw()
    }

    currentXAxisDomain() {
        const xAxis = this.chartResponse.configuration.axisByLabel("x", this.xAxisValue)
        const xScaleType = xAxis.scaleTypeName
        const points = this.lines.flatMap(line => line.data)
        const xDomain = xAxis.domainFor(points, 0)

        return { xDomain, xScaleType }
    }

    bindLegendInteractions() {
        this.legendContainerTarget.querySelectorAll(".line-chart-legend").forEach(legend => {
            const lineKey = legend.dataset.lineKey
            const disabled = this.hiddenLineKeys.has(lineKey)

            legend.style.opacity = disabled ? "0.35" : "1"
            legend.classList.toggle("disabled", disabled)

            legend.addEventListener("mouseenter", () => this.highlightLegendLine(lineKey))
            legend.addEventListener("mouseleave", () => this.clearLegendLineHighlight())
            legend.addEventListener("click", async () => {
                if (this.hiddenLineKeys.has(lineKey)) {
                    this.hiddenLineKeys.delete(lineKey)
                } else {
                    this.hiddenLineKeys.add(lineKey)
                }
                await this.draw()
            })
        })
    }

    highlightLegendLine(lineKey) {
        const line = this.lines.find(line => line.key === lineKey)
        this.focusYAxis(line?.options.secondary_axis ? "secondary" : "primary")

        d3.select(this.chartTarget)
            .selectAll(".line-series")
            .style("opacity", function () {
                return this.dataset.lineKey === lineKey ? 1 : 0.15
            })

        this.legendContainerTarget.querySelectorAll(".line-chart-legend").forEach(legend => {
            legend.style.opacity = legend.dataset.lineKey === lineKey ? "1" : "0.35"
        })
    }

    clearLegendLineHighlight() {
        this.focusYAxis(null)

        d3.select(this.chartTarget)
            .selectAll(".line-series")
            .style("opacity", 1)

        this.legendContainerTarget.querySelectorAll(".line-chart-legend").forEach(legend => {
            legend.style.opacity = this.hiddenLineKeys.has(legend.dataset.lineKey) ? "0.35" : "1"
        })
    }

    bindAxisHoverInteractions() {
        if (!this.secondaryYScale) return

        const chart = d3.select(this.chartTarget)
        chart.selectAll(".y-axis, .y-axis-label")
            .on("mouseenter", () => this.highlightYAxis("primary"))
            .on("mouseleave", () => this.clearLegendLineHighlight())
        chart.selectAll(".secondary-y-axis, .secondary-y-axis-label")
            .on("mouseenter", () => this.highlightYAxis("secondary"))
            .on("mouseleave", () => this.clearLegendLineHighlight())
        chart.selectAll(".line-series")
            .on("mouseenter", event => this.highlightLegendLine(event.currentTarget.dataset.lineKey))
            .on("mouseleave", () => this.clearLegendLineHighlight())
    }

    highlightYAxis(axis) {
        this.focusYAxis(axis)
        const secondaryKeys = new Set(this.lines.filter(line => line.options.secondary_axis).map(line => line.key))
        const focusedKey = key => secondaryKeys.has(key) === (axis === "secondary")

        d3.select(this.chartTarget).selectAll(".line-series")
            .style("opacity", function () { return focusedKey(this.dataset.lineKey) ? 1 : 0.15 })
        this.legendContainerTarget.querySelectorAll(".line-chart-legend").forEach(legend => {
            legend.style.opacity = focusedKey(legend.dataset.lineKey) ? "1" : "0.35"
        })
    }

    focusYAxis(axis) {
        if (!this.secondaryYScale) return

        this.focusedYAxis = axis
        const chart = d3.select(this.chartTarget)
        chart.selectAll(".y-axis, .y-axis-label").style("opacity", axis === "secondary" ? 0.15 : 1)
        chart.selectAll(".secondary-y-axis, .secondary-y-axis-label").style("opacity", axis === "primary" ? 0.15 : 1)
    }

    drawHoverOverlay() {
        const chart = d3.select(this.chartTarget)
        const chartTop = this.layersAboveSize + this.margin.top
        const chartBottom = chartTop + this.innerHeight
        const chartLeft = this.margin.left
        const chartRight = chartLeft + this.innerWidth
        const xAxisConfig = this.chartResponse.configuration.axisByLabel("x", this.xAxisValue)
        const yAxisConfig = this.chartResponse.configuration.axisByLabel("y", this.yAxisValue)
        const xAxisSide = xAxisConfig.position === "top" ? "top" : "bottom"
        const yAxisSide = yAxisConfig.position === "right" ? "right" : "left"
        const xAxisY = xAxisSide === "top" ? chartTop : chartBottom
        const yAxisX = yAxisSide === "right" ? chartRight : chartLeft

        let overlayPath = chart.select("path.overlay-path")
        let horizontalOverlayPath = chart.select("path.overlay-horizontal-path")
        let xAxisHoverLabel = chart.select("g.x-axis-hover-label")
        let yAxisHoverLabel = chart.select("g.y-axis-hover-label")

        if (overlayPath.empty()) {
            overlayPath = chart
                .append("path")
                .attr("class", "overlay-path")
                .attr("fill", "none")
                .attr("stroke", "grey")
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "4 4")
                .attr("pointer-events", "none")
        }

        if (horizontalOverlayPath.empty()) {
            horizontalOverlayPath = chart
                .append("path")
                .attr("class", "overlay-horizontal-path")
                .attr("fill", "none")
                .attr("stroke", "grey")
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "4 4")
                .attr("pointer-events", "none")
        }

        if (xAxisHoverLabel.empty()) xAxisHoverLabel = this.createHoverLabel(chart, "x-axis-hover-label")
        if (yAxisHoverLabel.empty()) yAxisHoverLabel = this.createHoverLabel(chart, "y-axis-hover-label")

        const lineGenerator = d3.line()
        const pointXPositions = [...new Set(this.linesData.flatMap(lineData => lineData.map(([x]) => x)))]

        chart.on("mousemove", (event) => {
            const [pointerX, pointerY] = d3.pointer(event, event.currentTarget)
            const innerPointerX = pointerX - this.margin.left
            const cursorY = Math.max(chartTop, Math.min(pointerY, chartBottom))

            let x
            let y = cursorY

            const nearestX = pointXPositions.reduce((nearest, pointX) => (
                nearest === null || Math.abs(pointX - innerPointerX) < Math.abs(nearest - innerPointerX) ? pointX : nearest
            ), null)
            const hoverX = nearestX !== null && Math.abs(nearestX - innerPointerX) <= 10 ? nearestX : null
            if (hoverX === null) {
                x = Math.max(
                    this.margin.left,
                    Math.min(pointerX, this.margin.left + this.innerWidth)
                )
            } else {
                x = this.margin.left + hoverX
                x = Math.max(
                    this.margin.left,
                    Math.min(x, this.margin.left + this.innerWidth)
                )
            }

            if (hoverX !== null) y = this.snappedYPosition(hoverX, cursorY, chartTop)

            this.setDataPointValues(hoverX)
            this.weatherLayerDrawer.setHoverX(pointerX)

            overlayPath
            .datum([
                [x, chartTop],
                [x, chartBottom]
            ])
            .attr("d", lineGenerator)

            horizontalOverlayPath
            .datum([
                [chartLeft, y],
                [chartRight, y]
            ])
            .attr("d", lineGenerator)

            this.positionHoverLabel({
                label: xAxisHoverLabel,
                text: this.xValueFormatter(this.xScale.invert(x - this.margin.left)),
                x,
                y: xAxisY,
                anchor: "middle",
                minX: chartLeft,
                maxX: chartRight,
                side: xAxisSide
            })

            const secondaryYFocused = this.focusedYAxis === "secondary"
            const hoverYScale = secondaryYFocused ? this.secondaryYScale : this.yScale
            const hoverYFormatter = secondaryYFocused ? this.secondaryYValueFormatter : this.yValueFormatter
            this.positionHoverLabel({
                label: yAxisHoverLabel,
                text: hoverYFormatter(hoverYScale.invert(y - chartTop)),
                x: secondaryYFocused ? chartRight : yAxisX,
                y,
                anchor: "middle",
                minX: chartLeft,
                maxX: chartRight,
                minY: chartTop,
                maxY: chartBottom,
                side: secondaryYFocused ? "right" : yAxisSide
            })
        })

        chart
            .on("mouseenter", () => {
                overlayPath.style("display", null)
                horizontalOverlayPath.style("display", null)
                xAxisHoverLabel.style("display", null)
                yAxisHoverLabel.style("display", null)
            })
            .on("mouseleave", () => {
                this.clearLegendLineHighlight()
                overlayPath.style("display", "none")
                horizontalOverlayPath.style("display", "none")
                xAxisHoverLabel.style("display", "none")
                yAxisHoverLabel.style("display", "none")
                this.weatherLayerDrawer.hideHover()
                this.weatherLayerDrawer.resetExpandedStack()
            })
    }

    snappedYPosition(hoverX, cursorY, chartTop) {
        const snapDistance = 10
        const innerCursorY = cursorY - chartTop
        const yPositions = this.linesData
            .map((lineData, lineIndex) => this.hiddenLineKeys.has(this.lineKeyFor(this.lines[lineIndex])) ? null : lineData.find(([x]) => x === hoverX)?.[1])
            .filter(Number.isFinite)

        if (yPositions.length === 0) return cursorY

        const nearestY = yPositions.reduce((nearest, y) => (
            Math.abs(y - innerCursorY) < Math.abs(nearest - innerCursorY) ? y : nearest
        ), yPositions[0])

        return Math.abs(nearestY - innerCursorY) <= snapDistance ? chartTop + nearestY : cursorY
    }

    createHoverLabel(chart, className) {
        const label = chart
            .append("g")
            .attr("class", className)
            .attr("pointer-events", "none")
            .style("display", "none")

        label
            .append("rect")
            .attr("rx", 4)
            .attr("fill", "#111827")
            .attr("opacity", 0.92)

        label
            .append("text")
            .attr("fill", "#ffffff")
            .attr("font-size", 11)
            .attr("font-weight", 600)
            .attr("dominant-baseline", "middle")

        return label
    }

    positionHoverLabel({ label, text, x, y, anchor, minX, maxX, minY, maxY, side }) {
        const paddingX = 6
        const paddingY = 4
        const textElement = label
            .select("text")
            .attr("text-anchor", anchor)
            .attr("x", 0)
            .attr("y", 0)
            .text(text)
            .node()
        const textBox = textElement.getBBox()
        const width = textBox.width + paddingX * 2
        const height = textBox.height + paddingY * 2
        let labelX = Math.max(minX + width / 2, Math.min(x, maxX - width / 2))
        let labelY = y

        if (side === "top") labelY = y - height / 2
        if (side === "bottom") labelY = y + height / 2
        if (side === "left") {
            labelX = x - width / 2
            labelY = Math.max(minY + height / 2, Math.min(y, maxY - height / 2))
        }
        if (side === "right") {
            labelX = x + width / 2
            labelY = Math.max(minY + height / 2, Math.min(y, maxY - height / 2))
        }

        label.attr("transform", `translate(${labelX}, ${labelY})`)
        label
            .select("rect")
            .attr("x", -width / 2)
            .attr("y", -height / 2)
            .attr("width", width)
            .attr("height", height)
        label
            .select("text")
            .attr("x", 0)
    }

    setDataPointValues(hoverX) {
        this.linesData.forEach((lineData, lineIndex) => {
            let values = this.element.querySelector(`.legend-values[data-index='${lineIndex}']`)
            let legendsDataElement = values.querySelector(".legend-data")
            let percentageElement = values.querySelector(".legend-percentage")
            const pointIndex = hoverX === null ? -1 : lineData.findIndex(([x]) => x === hoverX)

            if(pointIndex < 0 || this.hiddenLineKeys.has(this.lineKeyFor(this.lines[lineIndex]))) {
                legendsDataElement.textContent = "--"
                if(percentageElement) percentageElement.textContent = "--%"
                percentageElement?.classList?.remove("positive")
                percentageElement?.classList?.remove("negative")
                return
            }

            const secondaryAxis = this.lines[lineIndex].options.secondary_axis
            const yScale = secondaryAxis ? this.secondaryYScale : this.yScale
            const yValueFormatter = secondaryAxis ? this.secondaryYValueFormatter : this.yValueFormatter
            let newValue = yScale.invert(lineData[pointIndex][1])
            legendsDataElement.textContent = yValueFormatter(newValue)

            if (pointIndex == 0) {
                if(percentageElement) percentageElement.textContent = "0%"
                percentageElement?.classList?.remove("positive")
                percentageElement?.classList?.remove("negative")
                return
            }
            let oldValue = yScale.invert(lineData[pointIndex - 1][1])
            let percentage = oldValue == 0 ? (newValue > 0 ? -1 : 0) : (oldValue - newValue) / oldValue
            let factor = 10 ** this.chartResponse.options.percentage_precision
            let sign = percentage == 0 ? "" : (percentage < 0 ? "+" : "-")
            if(percentageElement) percentageElement.textContent = sign + Math.abs((Math.round((percentage * factor * 100)) / factor)) + "%"
            percentageElement?.classList?.toggle("positive", percentage < 0)
            percentageElement?.classList?.toggle("negative", percentage > 0)
        })
    }

    lineKeyFor(line) {
        return line.key
    }
}
